import { createClient } from '@supabase/supabase-js'

// Get environment variables with fallback to project credentials
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://zmnrjxqpdvhoaogmnybh.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InptbnJqeHFwZHZob2FvZ21ueWJoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTEzODAzNDQsImV4cCI6MjA2Njk1NjM0NH0.AHsLpSY3ihbWxVP9xam-n9YJbOWVNpmx5fi-sVjj_7s'

if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
  console.warn('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. Using fallback placeholder client.')
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types
export interface Inquiry {
  id?: string
  name: string
  email: string
  phone?: string
  course_interest?: string
  message: string
  created_at?: string
}

export interface Testimonial {
  id?: string
  student_name: string
  course_completed: string
  rating: number
  testimonial: string
  image_url?: string
  is_featured?: boolean
  created_at?: string
}

export interface Course {
  id?: string
  title: string
  level: string
  mode: string
  duration?: string
  description?: string
  price?: number          // Discounted / current selling price
  original_price?: number // Original (crossed-out) price before discount
  discounted_price?: number // Explicit discounted price (alias for price when both are set)
  cultural_component?: string
  is_active?: boolean
  // New detailed fields
  overview_description?: string
  learning_objectives?: LearningObjective[]
  course_features?: string[]
  curriculum?: CurriculumWeek[]
  instructor_info?: InstructorInfo
  course_reviews?: CourseReview[]
  course_stats?: CourseStats
  pricing_info?: PricingInfo
  enrollment_info?: EnrollmentInfo
  image_url?: string
  video_preview_url?: string
  course_highlights?: CourseHighlight[]
  prerequisites?: string
  certificate_info?: string
  max_students?: number
  rating?: number
  total_reviews?: number
  total_students?: number
  completion_rate?: number
  difficulty_level?: string
  language_focus?: string
  schedule_flexibility?: string
  course_format?: string
  materials_included?: string[]
  support_available?: string[]
  created_at?: string
  updated_at?: string
}

// Course Session Interface
export interface CourseSession {
  id?: string
  course_id: string
  session_name: string // e.g., "January 2025", "February 2025"
  delivery_mode: 'online' | 'physical'
  start_date: string // YYYY-MM-DD format
  end_date: string // YYYY-MM-DD format
  enrollment_start_date?: string
  enrollment_end_date?: string
  max_students?: number
  current_enrollment?: number
  session_fee?: number // Can override course price
  status?: 'upcoming' | 'enrollment_open' | 'enrollment_closed' | 'active' | 'completed' | 'cancelled'
  description?: string
  schedule_details?: {
    days_of_week?: string[] // e.g., ["Monday", "Wednesday", "Friday"]
    class_time?: string // e.g., "10:00 AM - 12:00 PM"
    duration_weeks?: number
    total_classes?: number
    class_duration_minutes?: number
  }
  instructor_notes?: string
  created_by?: string
  created_at?: string
  updated_at?: string
  // Relations
  course?: Course
  created_by_user?: {
    id: string
    name: string
    email: string
  }
}

// Supporting interfaces for Course details
export interface LearningObjective {
  title: string
  description?: string
  icon?: string
}

export interface CurriculumWeek {
  week: number
  title: string
  topics: string[]
  duration: string
  description?: string
}

export interface InstructorInfo {
  name: string
  title: string
  experience: string
  qualifications: string[]
  bio: string
  image: string
  specializations?: string[]
  languages?: string[]
}

export interface CourseReview {
  id: number
  name: string
  rating: number
  comment: string
  date: string
  course_completed?: string
  verified?: boolean
}

export interface CourseStats {
  completion_rate: number
  student_count: number
  average_rating: number
  total_reviews: number
  success_rate?: number
  job_placement_rate?: number
}

export interface PricingInfo {
  regular_price: number
  discounted_price?: number
  discount_percentage?: number
  payment_plans?: PaymentPlan[]
  scholarships_available?: boolean
  early_bird_discount?: number
}

export interface PaymentPlan {
  name: string
  amount: number
  duration: string
  description?: string
}

export interface EnrollmentInfo {
  enrollment_deadline?: string
  start_date?: string
  available_slots?: number
  requirements?: string[]
  application_process?: string[]
}

export interface CourseHighlight {
  icon: string
  title: string
  description: string
  value?: string
}

export interface AdmissionApplication {
  id?: string
  full_name: string
  gender: 'Male' | 'Female' | 'Others'
  date_of_birth: string // YYYY-MM-DD format
  contact_number: string
  email_address: string
  temporary_address: string
  language_course_interest: string
  language_level: 'A1' | 'A2' | 'B1' | 'B2'
  course_delivery_mode?: 'online' | 'physical' | 'hybrid'
  session_id?: string // Reference to the specific course session (legacy)
  enrollment_month?: string // Format: "2025-01" for simple month-based enrollment
  shift?: 'morning' | 'day' | 'evening' | string
  time_slot?: string
  requests?: string
  application_date?: string
  applicant_signature?: string
  status?: 'pending' | 'under_review' | 'approved' | 'rejected' | 'waitlisted'
  payment_method?: 'qr_code' | 'bank_transfer' | 'pay_later' | 'pending'
  payment_status?: 'pending' | 'paid' | 'failed' | 'refunded'
  payment_reference?: string
  payment_amount?: number
  payment_date?: string
  payment_notes?: string
  notes?: string
  created_at?: string
  updated_at?: string
  // Relations
  session?: CourseSession
}

export interface PaymentSettings {
  id?: string
  qr_code_image_url?: string
  bank_name: string
  account_name: string
  account_number: string
  branch_name: string
  course_fee?: number  // Made optional since course fees are determined by individual courses
  whatsapp_number?: string  // WhatsApp number for customer support
  created_at?: string
  updated_at?: string
}

// Blog system interfaces
export interface BlogCategory {
  id?: string
  name: string
  slug: string
  description?: string
  color?: string
  display_order?: number
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export interface BlogTag {
  id?: string
  name: string
  slug: string
  description?: string
  color?: string
  usage_count?: number
  created_at?: string
  updated_at?: string
}

export interface BlogPost {
  id?: string
  title: string
  slug: string
  content: string
  excerpt?: string
  featured_image_url?: string
  author_id?: string
  status: 'draft' | 'published' | 'archived'
  is_featured?: boolean
  view_count?: number
  reading_time?: number
  meta_title?: string
  meta_description?: string
  published_at?: string
  created_at?: string
  updated_at?: string
  // Relations
  author?: {
    id: string
    name: string
    email: string
  }
  categories?: BlogCategory[]
  tags?: BlogTag[]
  comment_count?: number
}

export interface BlogComment {
  id?: string
  post_id: string
  parent_id?: string
  author_name: string
  author_email: string
  author_website?: string
  content: string
  status: 'pending' | 'approved' | 'spam' | 'rejected'
  ip_address?: string
  user_agent?: string
  created_at?: string
  updated_at?: string
  // Relations
  replies?: BlogComment[]
  reply_count?: number
}

export interface BlogPostCategory {
  id?: string
  post_id: string
  category_id: string
  created_at?: string
}

export interface BlogPostTag {
  id?: string
  post_id: string
  tag_id: string
  created_at?: string
}

// Blog query interfaces
export interface BlogPostsQuery {
  page?: number
  limit?: number
  category?: string
  tag?: string
  search?: string
  status?: 'draft' | 'published' | 'archived'
  featured?: boolean
  author_id?: string
  sort_by?: 'created_at' | 'published_at' | 'view_count' | 'title'
  sort_order?: 'asc' | 'desc'
}

export interface BlogPostsResponse {
  posts: BlogPost[]
  total: number
  page: number
  limit: number
  total_pages: number
}

// API functions
export const submitInquiry = async (inquiry: Inquiry) => {
  const { error } = await supabase
    .from('inquiries')
    .insert([inquiry])

  if (error) throw error
}

export const getTestimonials = async (featured_only = false) => {
  let query = supabase.from('testimonials').select('*')

  if (featured_only) {
    query = query.eq('is_featured', true)
  }

  const { data, error } = await query.order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export const getCourses = async (includeInactive = false) => {
  let query = supabase
    .from('courses')
    .select('*')

  // Only filter by is_active if we don't want inactive courses
  if (!includeInactive) {
    query = query.or('is_active.eq.true,is_active.is.null')
  }

  const { data, error } = await query.order('level')

  if (error) throw error
  return data
}

export const updateCourse = async (id: string, updates: Partial<Course>) => {
  const { data, error } = await supabase
    .from('courses')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as Course
}

export const createCourse = async (course: Omit<Course, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('courses')
    .insert([course])
    .select()

  if (error) throw error
  return data[0] as Course
}

export const deleteCourse = async (id: string) => {
  const { error } = await supabase
    .from('courses')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export const submitAdmissionApplication = async (application: AdmissionApplication) => {
  let payload: any = { ...application }
  let { data, error } = await supabase
    .from('admission_applications')
    .insert([payload])
    .select()

  if (error && (
    error.message?.includes('shift') ||
    error.message?.includes('time_slot') ||
    error.message?.includes('notes') ||
    error.code === 'PGRST204' ||
    error.code === '42703'
  )) {
    if (error.message?.includes('notes')) delete payload.notes
    if (error.message?.includes('shift')) delete payload.shift
    if (error.message?.includes('time_slot')) delete payload.time_slot
    if (error.code === 'PGRST204') {
      delete payload.notes
      delete payload.shift
      delete payload.time_slot
    }
    const retry = await supabase
      .from('admission_applications')
      .insert([payload])
      .select()
    data = retry.data
    error = retry.error
  }

  if (error) throw error
  return data
}

export const getAdmissionApplications = async () => {
  const { data, error } = await supabase
    .from('admission_applications')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export const updateAdmissionApplication = async (id: string, updates: Partial<AdmissionApplication>) => {
  const payload: any = { ...updates }
  if (payload.notes !== undefined && !payload.payment_notes) {
    payload.payment_notes = payload.notes
  }

  let { data, error } = await supabase
    .from('admission_applications')
    .update(payload)
    .eq('id', id)
    .select()

  if (error && (
    error.message?.includes('notes') ||
    error.message?.includes('shift') ||
    error.message?.includes('time_slot') ||
    error.code === 'PGRST204' ||
    error.code === '42703'
  )) {
    if (error.message?.includes('notes')) delete payload.notes
    if (error.message?.includes('shift')) delete payload.shift
    if (error.message?.includes('time_slot')) delete payload.time_slot
    if (error.code === 'PGRST204') {
      delete payload.notes
      delete payload.shift
      delete payload.time_slot
    }
    const retry = await supabase
      .from('admission_applications')
      .update(payload)
      .eq('id', id)
      .select()
    data = retry.data
    error = retry.error
  }

  if (error) throw error
  return data
}

export const getPaymentSettings = async (): Promise<PaymentSettings | null> => {
  try {
    const { data, error } = await supabase
      .from('payment_settings')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)

    if (error) {
      console.error('Error fetching payment settings:', error)
      // If no settings exist, return default settings
      if (error.code === 'PGRST116') {
        console.warn('No payment settings found, using defaults')
        return {
          bank_name: 'Nepal Investment Bank',
          account_name: 'Nepal World Language Center',
          account_number: '1234567890123456',
          branch_name: 'Kathmandu Main Branch'
          // course_fee removed - determined by individual courses
        }
      }
      throw error
    }

    // Handle both single result and array result
    const settings = Array.isArray(data) ? data[0] : data

    if (!settings) {
      console.warn('No payment settings found, using defaults')
      return {
        bank_name: 'Nepal Investment Bank',
        account_name: 'Nepal World Language Center',
        account_number: '1234567890123456',
        branch_name: 'Kathmandu Main Branch'
      }
    }

    console.log('Payment settings loaded successfully:', settings)
    return settings
  } catch (err) {
    console.error('Failed to fetch payment settings:', err)
    // Return defaults on any error
    return {
      bank_name: 'Nepal Investment Bank',
      account_name: 'Nepal World Language Center',
      account_number: '1234567890123456',
      branch_name: 'Kathmandu Main Branch'
    }
  }
}

export const updatePaymentSettings = async (settings: Partial<PaymentSettings>) => {
  // First try to get existing settings
  const { data: existing } = await supabase
    .from('payment_settings')
    .select('id')
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (existing?.id) {
    // Update existing settings
    const { data, error } = await supabase
      .from('payment_settings')
      .update({
        ...settings,
        updated_at: new Date().toISOString()
      })
      .eq('id', existing.id)
      .select()

    if (error) throw error
    return data
  } else {
    // Create new settings
    const { data, error } = await supabase
      .from('payment_settings')
      .insert([settings])
      .select()

    if (error) throw error
    return data
  }
}

// Blog API functions

// Blog Categories
export const getBlogCategories = async (activeOnly = true) => {
  let query = supabase.from('blog_categories').select('*')

  if (activeOnly) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query.order('display_order', { ascending: true })

  if (error) throw error
  return data as BlogCategory[]
}

export const createBlogCategory = async (category: Omit<BlogCategory, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('blog_categories')
    .insert([category])
    .select()

  if (error) throw error
  return data[0] as BlogCategory
}

export const updateBlogCategory = async (id: string, updates: Partial<BlogCategory>) => {
  const { data, error } = await supabase
    .from('blog_categories')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as BlogCategory
}

export const deleteBlogCategory = async (id: string) => {
  const { error } = await supabase
    .from('blog_categories')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Blog Tags
export const getBlogTags = async (limit?: number) => {
  let query = supabase.from('blog_tags').select('*')

  if (limit) {
    query = query.limit(limit)
  }

  const { data, error } = await query.order('usage_count', { ascending: false })

  if (error) throw error
  return data as BlogTag[]
}

export const createBlogTag = async (tag: Omit<BlogTag, 'id' | 'created_at' | 'updated_at' | 'usage_count'>) => {
  const { data, error } = await supabase
    .from('blog_tags')
    .insert([tag])
    .select()

  if (error) throw error
  return data[0] as BlogTag
}

export const updateBlogTag = async (id: string, updates: Partial<BlogTag>) => {
  const { data, error } = await supabase
    .from('blog_tags')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as BlogTag
}

export const deleteBlogTag = async (id: string) => {
  const { error } = await supabase
    .from('blog_tags')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Blog Posts
export const getBlogPosts = async (query: BlogPostsQuery = {}): Promise<BlogPostsResponse> => {
  const {
    page = 1,
    limit = 10,
    search,
    status = 'published',
    featured,
    author_id,
    sort_by = 'published_at',
    sort_order = 'desc'
  } = query

  let supabaseQuery = supabase
    .from('blog_posts')
    .select(`
      *,
      author:admin_users(id, name, email),
      categories:blog_post_categories(
        blog_categories(id, name, slug, color)
      ),
      tags:blog_post_tags(
        blog_tags(id, name, slug, color)
      )
    `)

  // Apply filters
  if (status) {
    supabaseQuery = supabaseQuery.eq('status', status)
  }

  if (featured !== undefined) {
    supabaseQuery = supabaseQuery.eq('is_featured', featured)
  }

  if (author_id) {
    supabaseQuery = supabaseQuery.eq('author_id', author_id)
  }

  if (search) {
    supabaseQuery = supabaseQuery.or(`title.ilike.%${search}%,content.ilike.%${search}%,excerpt.ilike.%${search}%`)
  }

  // Apply sorting
  supabaseQuery = supabaseQuery.order(sort_by, { ascending: sort_order === 'asc' })

  // Apply pagination
  const from = (page - 1) * limit
  const to = from + limit - 1
  supabaseQuery = supabaseQuery.range(from, to)

  const { data, error } = await supabaseQuery

  if (error) throw error

  // Get total count for pagination
  const { count: totalCount } = await supabase
    .from('blog_posts')
    .select('*', { count: 'exact', head: true })
    .eq('status', status)

  // Transform nested categories and tags
  const transformedPosts = (data as any[]).map(post => ({
    ...post,
    categories: post.categories?.map((c: any) => c.blog_categories).filter(Boolean) || [],
    tags: post.tags?.map((t: any) => t.blog_tags).filter(Boolean) || []
  }))

  return {
    posts: transformedPosts as BlogPost[],
    total: totalCount || 0,
    page,
    limit,
    total_pages: Math.ceil((totalCount || 0) / limit)
  }
}

export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      author:admin_users(id, name, email),
      categories:blog_post_categories(
        blog_categories(id, name, slug, color)
      ),
      tags:blog_post_tags(
        blog_tags(id, name, slug, color)
      )
    `)
    .eq('slug', slug)
    .eq('status', 'published')
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }

  const transformedPost = {
    ...data,
    categories: (data as any).categories?.map((c: any) => c.blog_categories).filter(Boolean) || [],
    tags: (data as any).tags?.map((t: any) => t.blog_tags).filter(Boolean) || []
  }

  return transformedPost as BlogPost
}

export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  if (!id || id === 'undefined' || id === 'new') return null

  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      author:admin_users(id, name, email),
      categories:blog_post_categories(
        blog_categories(id, name, slug, color)
      ),
      tags:blog_post_tags(
        blog_tags(id, name, slug, color)
      )
    `)
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }

  const transformedPost = {
    ...data,
    categories: (data as any).categories?.map((c: any) => c.blog_categories).filter(Boolean) || [],
    tags: (data as any).tags?.map((t: any) => t.blog_tags).filter(Boolean) || []
  }

  return transformedPost as BlogPost
}

export const createBlogPost = async (post: any) => {
  // Strip non-column relation properties
  const { categories, tags, author, id, created_at, updated_at, view_count, ...cleanPost } = post

  // Ensure author_id if available
  if (!cleanPost.author_id) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user?.id) {
        cleanPost.author_id = session.user.id
      }
    } catch {
      // Ignore session fetch errors
    }
  }

  if (cleanPost.reading_time) {
    cleanPost.reading_time = Math.round(Number(cleanPost.reading_time)) || 1
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .insert([cleanPost])
    .select()

  if (error) {
    console.error('Supabase error creating blog post:', error)
    throw new Error(error.message || 'Failed to create blog post')
  }

  if (!data || data.length === 0) {
    throw new Error('No post data returned after insert')
  }

  return data[0] as BlogPost
}

export const updateBlogPost = async (id: string, updates: any) => {
  if (!id || id === 'undefined') throw new Error('Invalid blog post ID')

  // Strip non-column relation properties
  const { categories, tags, author, created_at, updated_at, ...cleanUpdates } = updates

  if (cleanUpdates.reading_time) {
    cleanUpdates.reading_time = Math.round(Number(cleanUpdates.reading_time)) || 1
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(cleanUpdates)
    .eq('id', id)
    .select()

  if (error) {
    console.error('Supabase error updating blog post:', error)
    throw new Error(error.message || 'Failed to update blog post')
  }

  if (!data || data.length === 0) {
    throw new Error('No post data returned after update')
  }

  return data[0] as BlogPost
}

export const deleteBlogPost = async (id: string) => {
  if (!id || id === 'undefined') return

  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Blog Post Categories and Tags Management
export const assignCategoriesToPost = async (postId: string, categoryIds: string[]) => {
  if (!postId || postId === 'undefined') return

  // First, remove existing categories
  await supabase
    .from('blog_post_categories')
    .delete()
    .eq('post_id', postId)

  // Then add new categories
  if (categoryIds.length > 0) {
    const assignments = categoryIds.map(categoryId => ({
      post_id: postId,
      category_id: categoryId
    }))

    const { error } = await supabase
      .from('blog_post_categories')
      .insert(assignments)

    if (error) throw error
  }
}

export const assignTagsToPost = async (postId: string, tagIds: string[]) => {
  if (!postId || postId === 'undefined') return

  // First, remove existing tags
  await supabase
    .from('blog_post_tags')
    .delete()
    .eq('post_id', postId)

  // Then add new tags
  if (tagIds.length > 0) {
    const assignments = tagIds.map(tagId => ({
      post_id: postId,
      tag_id: tagId
    }))

    const { error } = await supabase
      .from('blog_post_tags')
      .insert(assignments)

    if (error) throw error
  }
}

// Blog Comments
export const getBlogComments = async (postId: string, status: 'approved' | 'all' = 'approved') => {
  let query = supabase
    .from('blog_comments')
    .select('*')
    .eq('post_id', postId)
    .is('parent_id', null) // Only get top-level comments
    .order('created_at', { ascending: true })

  if (status === 'approved') {
    query = query.eq('status', 'approved')
  }

  const { data, error } = await query

  if (error) throw error

  // Get replies for each comment
  const commentsWithReplies = await Promise.all(
    (data || []).map(async (comment) => {
      const { data: replies, error: repliesError } = await supabase
        .from('blog_comments')
        .select('*')
        .eq('parent_id', comment.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: true })

      if (repliesError) throw repliesError

      return {
        ...comment,
        replies: replies || [],
        reply_count: replies?.length || 0
      }
    })
  )

  return commentsWithReplies as BlogComment[]
}

export const createBlogComment = async (comment: Omit<BlogComment, 'id' | 'created_at' | 'updated_at' | 'status'>) => {
  const { data, error } = await supabase
    .from('blog_comments')
    .insert([{ ...comment, status: 'pending' }])
    .select()

  if (error) throw error
  return data[0] as BlogComment
}

export const updateBlogCommentStatus = async (id: string, status: BlogComment['status']) => {
  const { data, error } = await supabase
    .from('blog_comments')
    .update({ status })
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as BlogComment
}

export const deleteBlogComment = async (id: string) => {
  const { error } = await supabase
    .from('blog_comments')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export const getPendingComments = async () => {
  const { data, error } = await supabase
    .from('blog_comments')
    .select(`
      *,
      blog_posts(title, slug)
    `)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as (BlogComment & { blog_posts: { title: string; slug: string } })[]
}

// Financial Management Interfaces
export interface FinancialCategory {
  id?: string
  name: string
  type: 'income' | 'expense'
  color?: string
  description?: string
  is_active?: boolean
  created_at?: string
}

export interface FinancialTransaction {
  id?: string
  category_id: string
  amount: number
  description: string
  party_name?: string // Name of the student or party involved in the transaction
  course_id?: string // Reference to the course associated with this transaction
  course_level?: string // Course level auto-filled from course data
  session_id?: string // Reference to the specific course session
  transaction_date: string // YYYY-MM-DD format
  type: 'income' | 'expense'
  created_by?: string
  created_at?: string
  updated_at?: string
  // Relations
  category?: FinancialCategory
  course?: Course // Course information
  session?: CourseSession // Session information
  created_by_user?: {
    id: string
    name: string
    email: string
  }
}

export interface FinancialSummary {
  total_income: number
  total_expenses: number
  net_profit: number
  transaction_count: number
  categories_breakdown: {
    category_name: string
    category_type: 'income' | 'expense'
    total_amount: number
    transaction_count: number
    color: string
  }[]
}

export interface FinancialReportQuery {
  start_date?: string
  end_date?: string
  category_id?: string
  type?: 'income' | 'expense'
  limit?: number
  offset?: number
}

// Financial Management API Functions

// Financial Categories
export const getFinancialCategories = async (type?: 'income' | 'expense', activeOnly = true) => {
  let query = supabase.from('financial_categories').select('*')

  if (type) {
    query = query.eq('type', type)
  }

  if (activeOnly) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query.order('name', { ascending: true })

  if (error) throw error
  return data as FinancialCategory[]
}

export const createFinancialCategory = async (category: Omit<FinancialCategory, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('financial_categories')
    .insert([category])
    .select()

  if (error) throw error
  return data[0] as FinancialCategory
}

export const updateFinancialCategory = async (id: string, updates: Partial<FinancialCategory>) => {
  const { data, error } = await supabase
    .from('financial_categories')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as FinancialCategory
}

export const deleteFinancialCategory = async (id: string) => {
  const { error } = await supabase
    .from('financial_categories')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Financial Transactions
export const getFinancialTransactions = async (query: FinancialReportQuery = {}) => {
  const {
    start_date,
    end_date,
    category_id,
    type,
    limit = 50,
    offset = 0
  } = query

  let supabaseQuery = supabase
    .from('financial_transactions')
    .select(`
      *,
      category:financial_categories(id, name, type, color),
      course:courses(id, title, level),
      created_by_user:admin_users(id, name, email)
    `)

  // Apply filters
  if (start_date) {
    supabaseQuery = supabaseQuery.gte('transaction_date', start_date)
  }

  if (end_date) {
    supabaseQuery = supabaseQuery.lte('transaction_date', end_date)
  }

  if (category_id) {
    supabaseQuery = supabaseQuery.eq('category_id', category_id)
  }

  if (type) {
    supabaseQuery = supabaseQuery.eq('type', type)
  }

  // Apply pagination and sorting
  const { data, error } = await supabaseQuery
    .order('transaction_date', { ascending: false })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as FinancialTransaction[]
}

export const createFinancialTransaction = async (transaction: Omit<FinancialTransaction, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('financial_transactions')
    .insert([transaction])
    .select(`
      *,
      category:financial_categories(id, name, type, color),
      course:courses(id, title, level),
      created_by_user:admin_users(id, name, email)
    `)

  if (error) throw error
  return data[0] as FinancialTransaction
}

export const updateFinancialTransaction = async (id: string, updates: Partial<FinancialTransaction>) => {
  const { data, error } = await supabase
    .from('financial_transactions')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      category:financial_categories(id, name, type, color),
      course:courses(id, title, level),
      created_by_user:admin_users(id, name, email)
    `)

  if (error) throw error
  return data[0] as FinancialTransaction
}

export const deleteFinancialTransaction = async (id: string) => {
  const { error } = await supabase
    .from('financial_transactions')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Financial Reports and Analytics
export const getFinancialSummary = async (start_date?: string, end_date?: string): Promise<FinancialSummary> => {
  let query = supabase
    .from('financial_transactions')
    .select(`
      amount,
      type,
      category:financial_categories(name, color)
    `)

  if (start_date) {
    query = query.gte('transaction_date', start_date)
  }

  if (end_date) {
    query = query.lte('transaction_date', end_date)
  }

  const { data, error } = await query

  if (error) throw error

  // Calculate summary statistics
  const transactions = data || []

  const total_income = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + Number(t.amount), 0)

  const total_expenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + Number(t.amount), 0)

  const net_profit = total_income - total_expenses

  // Calculate category breakdown
  const categoryMap = new Map<string, {
    category_name: string
    category_type: 'income' | 'expense'
    total_amount: number
    transaction_count: number
    color: string
  }>()

  transactions.forEach(transaction => {
    const categoryData = Array.isArray(transaction.category) ? transaction.category[0] : transaction.category
    const key = categoryData?.name || 'Unknown'
    const existing = categoryMap.get(key)

    if (existing) {
      existing.total_amount += Number(transaction.amount)
      existing.transaction_count += 1
    } else {
      categoryMap.set(key, {
        category_name: key,
        category_type: transaction.type as 'income' | 'expense',
        total_amount: Number(transaction.amount),
        transaction_count: 1,
        color: categoryData?.color || '#6B7280'
      })
    }
  })

  const categories_breakdown = Array.from(categoryMap.values())
    .sort((a, b) => b.total_amount - a.total_amount)

  return {
    total_income,
    total_expenses,
    net_profit,
    transaction_count: transactions.length,
    categories_breakdown
  }
}

export const getMonthlyFinancialReport = async (year: number, month: number) => {
  const start_date = `${year}-${month.toString().padStart(2, '0')}-01`
  const end_date = new Date(year, month, 0).toISOString().split('T')[0] // Last day of month

  return await getFinancialSummary(start_date, end_date)
}

export const getYearlyFinancialReport = async (year: number) => {
  const start_date = `${year}-01-01`
  const end_date = `${year}-12-31`

  return await getFinancialSummary(start_date, end_date)
}

// Library Management Interfaces
export interface LibraryBook {
  id?: string
  title: string
  author: string
  isbn?: string
  publisher?: string
  publication_year?: number
  category: string
  language?: string
  total_copies: number
  available_copies: number
  location?: string
  description?: string
  cover_image_url?: string
  price?: number
  status?: 'active' | 'inactive' | 'damaged' | 'lost'
  created_at?: string
  updated_at?: string
}

export interface LibraryMember {
  id?: string
  member_id: string
  full_name: string
  email?: string
  phone?: string
  address?: string
  member_type?: 'student' | 'teacher' | 'staff' | 'external'
  student_id?: string
  department?: string
  membership_date?: string
  expiry_date?: string
  status?: 'active' | 'inactive' | 'suspended'
  max_books_allowed?: number
  fine_amount?: number
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface LibraryBorrowing {
  id?: string
  book_id: string
  member_id: string
  borrowed_date?: string
  due_date: string
  returned_date?: string
  status?: 'borrowed' | 'returned' | 'overdue' | 'lost'
  fine_amount?: number
  notes?: string
  issued_by?: string
  returned_to?: string
  created_at?: string
  updated_at?: string
  // Relations
  book?: LibraryBook
  member?: LibraryMember
  issued_by_user?: {
    id: string
    name: string
    email: string
  }
  returned_to_user?: {
    id: string
    name: string
    email: string
  }
}

export interface LibraryCategory {
  id?: string
  name: string
  description?: string
  color?: string
  is_active?: boolean
  created_at?: string
}

export interface LibraryStats {
  total_books: number
  total_members: number
  books_borrowed: number
  books_available: number
  overdue_books: number
  total_fines: number
  popular_books: {
    book_id: string
    title: string
    author: string
    borrow_count: number
  }[]
  recent_borrowings: LibraryBorrowing[]
}

export interface LibrarySearchQuery {
  query?: string
  category?: string
  author?: string
  status?: string
  member_type?: string
  limit?: number
  offset?: number
}

// Library API Functions

// Books Management
export const getLibraryBooks = async (searchQuery: LibrarySearchQuery = {}) => {
  const { query, category, author, status = 'active', limit = 50, offset = 0 } = searchQuery

  let supabaseQuery = supabase
    .from('library_books')
    .select('*')

  if (query) {
    supabaseQuery = supabaseQuery.or(`title.ilike.%${query}%,author.ilike.%${query}%,isbn.ilike.%${query}%`)
  }

  if (category) {
    supabaseQuery = supabaseQuery.eq('category', category)
  }

  if (author) {
    supabaseQuery = supabaseQuery.ilike('author', `%${author}%`)
  }

  if (status) {
    supabaseQuery = supabaseQuery.eq('status', status)
  }

  const { data, error } = await supabaseQuery
    .order('title', { ascending: true })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as LibraryBook[]
}

export const createLibraryBook = async (book: Omit<LibraryBook, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('library_books')
    .insert([book])
    .select()

  if (error) throw error
  return data[0] as LibraryBook
}

export const updateLibraryBook = async (id: string, updates: Partial<LibraryBook>) => {
  const { data, error } = await supabase
    .from('library_books')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as LibraryBook
}

export const deleteLibraryBook = async (id: string) => {
  const { error } = await supabase
    .from('library_books')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Members Management
export const getLibraryMembers = async (searchQuery: LibrarySearchQuery = {}) => {
  const { query, member_type, status = 'active', limit = 50, offset = 0 } = searchQuery

  let supabaseQuery = supabase
    .from('library_members')
    .select('*')

  if (query) {
    supabaseQuery = supabaseQuery.or(`full_name.ilike.%${query}%,member_id.ilike.%${query}%,email.ilike.%${query}%`)
  }

  if (member_type) {
    supabaseQuery = supabaseQuery.eq('member_type', member_type)
  }

  if (status) {
    supabaseQuery = supabaseQuery.eq('status', status)
  }

  const { data, error } = await supabaseQuery
    .order('full_name', { ascending: true })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as LibraryMember[]
}

export const createLibraryMember = async (member: Omit<LibraryMember, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('library_members')
    .insert([member])
    .select()

  if (error) throw error
  return data[0] as LibraryMember
}

export const updateLibraryMember = async (id: string, updates: Partial<LibraryMember>) => {
  const { data, error } = await supabase
    .from('library_members')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as LibraryMember
}

export const deleteLibraryMember = async (id: string) => {
  const { error } = await supabase
    .from('library_members')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Borrowings Management
export const getLibraryBorrowings = async (searchQuery: LibrarySearchQuery = {}) => {
  const { status, limit = 50, offset = 0 } = searchQuery

  let supabaseQuery = supabase
    .from('library_borrowings')
    .select(`
      *,
      book:library_books(id, title, author, isbn),
      member:library_members(id, member_id, full_name, email),
      issued_by_user:admin_users!issued_by(id, name, email),
      returned_to_user:admin_users!returned_to(id, name, email)
    `)

  if (status) {
    supabaseQuery = supabaseQuery.eq('status', status)
  }

  const { data, error } = await supabaseQuery
    .order('borrowed_date', { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as LibraryBorrowing[]
}

export const borrowBook = async (borrowing: Omit<LibraryBorrowing, 'id' | 'created_at' | 'updated_at'>) => {
  // Start a transaction to update both borrowings and book availability
  const { data: bookData, error: bookError } = await supabase
    .from('library_books')
    .select('available_copies')
    .eq('id', borrowing.book_id)
    .single()

  if (bookError) throw bookError
  if (bookData.available_copies <= 0) {
    throw new Error('No copies available for borrowing')
  }

  // Create borrowing record
  const { data: borrowingData, error: borrowingError } = await supabase
    .from('library_borrowings')
    .insert([borrowing])
    .select(`
      *,
      book:library_books(id, title, author, isbn),
      member:library_members(id, member_id, full_name, email)
    `)

  if (borrowingError) throw borrowingError

  // Update book availability
  const { error: updateError } = await supabase
    .from('library_books')
    .update({ available_copies: bookData.available_copies - 1 })
    .eq('id', borrowing.book_id)

  if (updateError) throw updateError

  return borrowingData[0] as LibraryBorrowing
}

export const returnBook = async (borrowingId: string, returnedBy?: string, fineAmount?: number) => {
  // Get borrowing details
  const { data: borrowingData, error: borrowingError } = await supabase
    .from('library_borrowings')
    .select('book_id')
    .eq('id', borrowingId)
    .single()

  if (borrowingError) throw borrowingError

  // Update borrowing record
  const updateData: any = {
    status: 'returned',
    returned_date: new Date().toISOString().split('T')[0]
  }

  if (returnedBy) updateData.returned_to = returnedBy
  if (fineAmount) updateData.fine_amount = fineAmount

  const { data, error: updateBorrowingError } = await supabase
    .from('library_borrowings')
    .update(updateData)
    .eq('id', borrowingId)
    .select(`
      *,
      book:library_books(id, title, author, isbn),
      member:library_members(id, member_id, full_name, email)
    `)

  if (updateBorrowingError) throw updateBorrowingError

  // Update book availability
  const { data: bookData, error: bookError } = await supabase
    .from('library_books')
    .select('available_copies')
    .eq('id', borrowingData.book_id)
    .single()

  if (bookError) throw bookError

  const { error: updateBookError } = await supabase
    .from('library_books')
    .update({ available_copies: bookData.available_copies + 1 })
    .eq('id', borrowingData.book_id)

  if (updateBookError) throw updateBookError

  return data[0] as LibraryBorrowing
}

// Categories Management
export const getLibraryCategories = async (activeOnly = true) => {
  let query = supabase.from('library_categories').select('*')

  if (activeOnly) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query.order('name', { ascending: true })

  if (error) throw error
  return data as LibraryCategory[]
}

export const createLibraryCategory = async (category: Omit<LibraryCategory, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('library_categories')
    .insert([category])
    .select()

  if (error) throw error
  return data[0] as LibraryCategory
}

export const updateLibraryCategory = async (id: string, updates: Partial<LibraryCategory>) => {
  const { data, error } = await supabase
    .from('library_categories')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as LibraryCategory
}

export const deleteLibraryCategory = async (id: string) => {
  const { error } = await supabase
    .from('library_categories')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Library Statistics
export const getLibraryStats = async (): Promise<LibraryStats> => {
  // Get basic counts
  const [booksResult, membersResult, borrowingsResult, overdueResult] = await Promise.all([
    supabase.from('library_books').select('id', { count: 'exact' }),
    supabase.from('library_members').select('id', { count: 'exact' }).eq('status', 'active'),
    supabase.from('library_borrowings').select('id', { count: 'exact' }).eq('status', 'borrowed'),
    supabase.from('library_borrowings').select('id', { count: 'exact' }).eq('status', 'overdue')
  ])

  // Get available books count
  const { data: availableBooksData } = await supabase
    .from('library_books')
    .select('available_copies')
    .eq('status', 'active')

  const books_available = availableBooksData?.reduce((sum, book) => sum + book.available_copies, 0) || 0

  // Get total fines
  const { data: finesData } = await supabase
    .from('library_borrowings')
    .select('fine_amount')

  const total_fines = finesData?.reduce((sum, borrowing) => sum + (borrowing.fine_amount || 0), 0) || 0

  // Get popular books (most borrowed)
  const { data: popularBooksData } = await supabase
    .from('library_borrowings')
    .select(`
      book_id,
      book:library_books(title, author)
    `)
    .limit(5)

  // Process popular books data
  const bookCounts = new Map()
  popularBooksData?.forEach(borrowing => {
    const count = bookCounts.get(borrowing.book_id) || 0
    bookCounts.set(borrowing.book_id, count + 1)
  })

  const popular_books = Array.from(bookCounts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([book_id, borrow_count]) => {
      const borrowingData = popularBooksData?.find(b => b.book_id === book_id)
      const bookData = borrowingData ? (Array.isArray(borrowingData.book) ? borrowingData.book[0] : borrowingData.book) : null
      return {
        book_id,
        title: bookData?.title || 'Unknown',
        author: bookData?.author || 'Unknown',
        borrow_count
      }
    })

  // Get recent borrowings
  const { data: recentBorrowingsData } = await supabase
    .from('library_borrowings')
    .select(`
      *,
      book:library_books(id, title, author),
      member:library_members(id, member_id, full_name)
    `)
    .order('borrowed_date', { ascending: false })
    .limit(10)

  return {
    total_books: booksResult.count || 0,
    total_members: membersResult.count || 0,
    books_borrowed: borrowingsResult.count || 0,
    books_available,
    overdue_books: overdueResult.count || 0,
    total_fines,
    popular_books,
    recent_borrowings: recentBorrowingsData as LibraryBorrowing[] || []
  }
}

// Update overdue books status
export const updateOverdueBooks = async () => {
  const today = new Date().toISOString().split('T')[0]

  const { error } = await supabase
    .from('library_borrowings')
    .update({ status: 'overdue' })
    .eq('status', 'borrowed')
    .lt('due_date', today)

  if (error) throw error
}

// Generate user-friendly student ID
const generateUserFriendlyStudentId = (fullName: string, applicationDate: string): string => {
  // Get initials from full name
  const nameParts = fullName.trim().split(' ')
  const initials = nameParts.map(part => part.charAt(0).toUpperCase()).join('')

  // Get year from application date
  const year = new Date(applicationDate).getFullYear().toString().slice(-2)

  // Generate a 4-digit number based on timestamp
  const timestamp = Date.now().toString().slice(-4)

  return `${initials}${year}${timestamp}`
}

// Auto-create library member from admission application
export const createLibraryMemberFromAdmission = async (application: AdmissionApplication) => {
  // Generate member ID
  const generateMemberId = (memberType: string): string => {
    const prefix = memberType.toUpperCase().substring(0, 3)
    const timestamp = Date.now().toString().slice(-6)
    return `${prefix}${timestamp}`
  }

  // Calculate membership expiry (1 year from now)
  const membershipDate = new Date().toISOString().split('T')[0]
  const expiryDate = new Date()
  expiryDate.setFullYear(expiryDate.getFullYear() + 1)
  const membershipExpiryDate = expiryDate.toISOString().split('T')[0]

  // Check if library member already exists for this application
  const { data: existingMember } = await supabase
    .from('library_members')
    .select('id')
    .eq('email', application.email_address)
    .eq('member_type', 'student')
    .single()

  if (existingMember) {
    // Member already exists, just return the existing member
    return existingMember
  }

  // Generate user-friendly student ID
  const userFriendlyStudentId = generateUserFriendlyStudentId(
    application.full_name,
    application.application_date || application.created_at || new Date().toISOString()
  )

  // Create new library member
  const libraryMember: Omit<LibraryMember, 'id' | 'created_at' | 'updated_at'> = {
    member_id: generateMemberId('student'),
    full_name: application.full_name,
    email: application.email_address,
    phone: application.contact_number,
    address: application.temporary_address,
    member_type: 'student',
    student_id: userFriendlyStudentId, // User-friendly student ID instead of UUID
    department: application.language_course_interest,
    membership_date: membershipDate,
    expiry_date: membershipExpiryDate,
    status: 'active',
    max_books_allowed: 3, // Default for students
    fine_amount: 0,
    notes: `Auto-created from admission application on ${membershipDate}`
  }

  const { data, error } = await supabase
    .from('library_members')
    .insert([libraryMember])
    .select()

  if (error) throw error
  return data[0] as LibraryMember
}

// Record confirmed admission payment into financial_transactions for finance tracking
export const recordAdmissionPaymentTransaction = async (application: Partial<AdmissionApplication>) => {
  try {
    const rawAmount = application.payment_amount
    const amount = typeof rawAmount === 'number'
      ? rawAmount
      : Number(String(rawAmount || '').replace(/,/g, '').trim())

    if (!amount || isNaN(amount) || amount <= 0) return null

    // 1. Get or create Course Fee category
    const categories = await getFinancialCategories('income').catch(() => [])
    let courseFeeCat = categories.find(c =>
      c.name.toLowerCase().includes('course') ||
      c.name.toLowerCase().includes('fee') ||
      c.name.toLowerCase().includes('admission') ||
      c.name.toLowerCase().includes('tuition')
    ) || categories[0]

    if (!courseFeeCat) {
      try {
        courseFeeCat = await createFinancialCategory({
          name: 'Course Fee',
          type: 'income',
          color: '#10B981',
          description: 'Course tuition and admission fees',
          is_active: true
        })
      } catch (catErr) {
        console.warn('Could not auto-create Course Fee category:', catErr)
      }
    }

    if (!courseFeeCat?.id) return null

    // 2. Resolve matching course and level
    let courseId: string | undefined = undefined
    let courseLevel = application.language_level || ''
    try {
      const allCourses = await getCourses(true)
      const matched = allCourses.find(c =>
        (c.id && (c.id === (application as any).selected_course_id || c.id === application.session_id)) ||
        (application.language_course_interest && c.title.toLowerCase().trim() === application.language_course_interest.toLowerCase().trim())
      )
      if (matched) {
        courseId = matched.id
        if (!courseLevel && matched.level) {
          courseLevel = matched.level
        }
      }
    } catch (e) {
      // ignore
    }

    const studentName = application.full_name || 'Student'
    const courseName = application.language_course_interest || 'Course'
    const todayStr = new Date().toISOString().split('T')[0]
    const transactionDate = application.payment_date
      ? application.payment_date.split('T')[0]
      : (application.application_date ? application.application_date.split('T')[0] : todayStr)

    const appIdTag = application.id ? ` [App: ${application.id.slice(0, 8)}]` : ''
    const refTag = application.payment_reference ? ` (Ref: ${application.payment_reference})` : ''
    const intakeTag = application.enrollment_month ? ` - Intake ${application.enrollment_month}` : ''
    const description = `Course fee: ${courseName}${intakeTag}${appIdTag}${refTag}`

    // 3. Avoid duplicate entries for the same application
    if (application.id) {
      const { data: existing } = await supabase
        .from('financial_transactions')
        .select('id')
        .eq('type', 'income')
        .ilike('description', `%${application.id.slice(0, 8)}%`)
        .limit(1)

      if (existing && existing.length > 0) {
        return existing[0] as FinancialTransaction
      }
    }

    // 4. Create financial transaction
    return await createFinancialTransaction({
      category_id: courseFeeCat.id,
      amount,
      description,
      party_name: studentName,
      course_id: courseId,
      course_level: courseLevel,
      transaction_date: transactionDate,
      type: 'income'
    })
  } catch (err) {
    console.warn('Error recording admission payment in finance:', err)
    return null
  }
}

// Enhanced admission application update with library member creation and financial integration
export const updateAdmissionApplicationWithLibraryIntegration = async (
  id: string,
  updates: Partial<AdmissionApplication>
) => {
  const payload: any = { ...updates }
  if (payload.notes !== undefined && !payload.payment_notes) {
    payload.payment_notes = payload.notes
  }

  // If payment is being confirmed as paid, stamp payment_date if not present
  if (payload.payment_status === 'paid' && !payload.payment_date) {
    payload.payment_date = new Date().toISOString()
  }

  // First update the admission application
  let { data: updatedApplication, error: updateError } = await supabase
    .from('admission_applications')
    .update(payload)
    .eq('id', id)
    .select()

  if (updateError && (updateError.message?.includes("'notes'") || updateError.code === 'PGRST204')) {
    delete payload.notes
    const retry = await supabase
      .from('admission_applications')
      .update(payload)
      .eq('id', id)
      .select()
    updatedApplication = retry.data
    updateError = retry.error
  }

  if (updateError) throw updateError

  const appData = (updatedApplication?.[0] || { id, ...updates }) as AdmissionApplication

  // If payment status is paid, automatically record income in the Finance page
  if (payload.payment_status === 'paid' || appData.payment_status === 'paid') {
    await recordAdmissionPaymentTransaction(appData)
  }

  return updatedApplication
}

// Get library member by admission application ID
export const getLibraryMemberByAdmissionId = async (admissionId: string) => {
  try {
    // First get the admission application to get the email
    const { data: application, error: appError } = await supabase
      .from('admission_applications')
      .select('email_address')
      .eq('id', admissionId)
      .single()

    if (appError) {
      console.error('Error fetching admission application:', appError)
      return null
    }

    // Then find library member by email
    const { data, error } = await supabase
      .from('library_members')
      .select('*')
      .eq('email', application.email_address)
      .eq('member_type', 'student')
      .maybeSingle()

    if (error) {
      console.error('Error fetching library member:', error)
      return null
    }

    return data as LibraryMember | null
  } catch (err) {
    console.error('Error in getLibraryMemberByAdmissionId:', err)
    return null
  }
}

// Check if admission application has associated library member
export const hasLibraryMember = async (admissionId: string): Promise<boolean> => {
  try {
    const member = await getLibraryMemberByAdmissionId(admissionId)
    return member !== null
  } catch (err) {
    console.error('Error checking library member status:', err)
    return false
  }
}

// Popup Management Interfaces
export interface PopupCategory {
  id?: string
  name: string
  description?: string
  color?: string
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export interface Popup {
  id?: string
  popup_type?: 'modal' | 'banner' | 'slide-in' | 'corner'
  category_id?: string

  // Display settings
  is_active?: boolean
  priority?: number
  max_displays?: number
  current_displays?: number

  // Timing settings
  delay_seconds?: number
  auto_close_seconds?: number
  display_frequency?: 'once_per_session' | 'once_per_day' | 'always' | 'once_per_user'
  show_frequency?: 'once_per_session' | 'once_per_day' | 'always' | 'once_per_user'

  // Targeting settings
  target_pages?: string[]
  target_user_types?: string[]
  exclude_pages?: string[]

  // Appearance settings
  background_color?: string
  text_color?: string
  border_color?: string
  button_color?: string
  button_text_color?: string

  // Size and position
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  height?: 'auto' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

  // Content settings
  image_url?: string
  button_text?: string
  button_action?: 'close' | 'redirect' | 'custom'
  button_url?: string
  custom_css?: string

  // Analytics
  view_count?: number
  click_count?: number
  conversion_rate?: number

  // Scheduling
  start_date?: string
  end_date?: string

  // Metadata
  created_by?: string
  created_at?: string
  updated_at?: string

  // Relations
  category?: PopupCategory
}

export interface PopupInteraction {
  id?: string
  popup_id: string
  session_id?: string
  user_ip?: string
  user_agent?: string
  interaction_type: 'viewed' | 'clicked' | 'closed' | 'dismissed'
  page_url?: string
  interaction_data?: any
  created_at?: string
  // Relations
  popup?: Popup
}

export interface PopupSettings {
  id?: string
  setting_key: string
  setting_value?: string
  setting_type?: 'string' | 'number' | 'boolean' | 'json'
  description?: string
  created_at?: string
  updated_at?: string
}

export interface PopupQuery {
  page?: number
  limit?: number
  category_id?: string
  is_active?: boolean
  popup_type?: string
  search?: string
  sort_by?: 'created_at' | 'updated_at' | 'priority' | 'title' | 'view_count'
  sort_order?: 'asc' | 'desc'
}

export interface PopupResponse {
  popups: Popup[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export interface PopupStats {
  total_popups: number
  active_popups: number
  total_views: number
  total_clicks: number
  average_conversion_rate: number
  popular_popups: {
    id: string
    title: string
    view_count: number
    click_count: number
    conversion_rate: number
  }[]
  recent_interactions: PopupInteraction[]
}

// Popup API Functions

// Popup Categories
export const getPopupCategories = async (activeOnly = true) => {
  let query = supabase.from('popup_categories').select('*')

  if (activeOnly) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query.order('name', { ascending: true })

  if (error) throw error
  return data as PopupCategory[]
}

export const createPopupCategory = async (category: Omit<PopupCategory, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('popup_categories')
    .insert([category])
    .select()

  if (error) throw error
  return data[0] as PopupCategory
}

export const updatePopupCategory = async (id: string, updates: Partial<PopupCategory>) => {
  const { data, error } = await supabase
    .from('popup_categories')
    .update(updates)
    .eq('id', id)
    .select()

  if (error) throw error
  return data[0] as PopupCategory
}

export const deletePopupCategory = async (id: string) => {
  const { error } = await supabase
    .from('popup_categories')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Popups
export const getPopups = async (query: PopupQuery = {}): Promise<PopupResponse> => {
  const {
    page = 1,
    limit = 10,
    category_id,
    is_active,
    popup_type,
    search,
    sort_by = 'created_at',
    sort_order = 'desc'
  } = query

  let supabaseQuery = supabase
    .from('popups')
    .select(`
      *,
      category:popup_categories(id, name, color)
    `)

  // Apply filters
  if (category_id) {
    supabaseQuery = supabaseQuery.eq('category_id', category_id)
  }

  if (is_active !== undefined) {
    supabaseQuery = supabaseQuery.eq('is_active', is_active)
  }

  if (popup_type) {
    supabaseQuery = supabaseQuery.eq('popup_type', popup_type)
  }

  if (search) {
    supabaseQuery = supabaseQuery.or(`title.ilike.%${search}%,content.ilike.%${search}%`)
  }

  // Apply sorting
  supabaseQuery = supabaseQuery.order(sort_by, { ascending: sort_order === 'asc' })

  // Apply pagination
  const from = (page - 1) * limit
  const to = from + limit - 1
  supabaseQuery = supabaseQuery.range(from, to)

  const { data, error } = await supabaseQuery

  if (error) throw error

  // Get total count for pagination
  const { count: totalCount } = await supabase
    .from('popups')
    .select('*', { count: 'exact', head: true })

  return {
    popups: data as Popup[],
    total: totalCount || 0,
    page,
    limit,
    total_pages: Math.ceil((totalCount || 0) / limit)
  }
}

export const getActivePopups = async (currentPath?: string): Promise<Popup[]> => {
  const now = new Date().toISOString()

  let query = supabase
    .from('popups')
    .select(`
      *,
      category:popup_categories(id, name, color)
    `)
    .eq('is_active', true)
    .or(`start_date.is.null,start_date.lte.${now}`)
    .or(`end_date.is.null,end_date.gte.${now}`)
    .order('priority', { ascending: false })

  const { data, error } = await query

  if (error) throw error

  // Filter by target pages if currentPath is provided
  if (currentPath && data) {
    return data.filter((popup: Popup) => {
      // If no target pages specified, show on all pages
      if (!popup.target_pages || popup.target_pages.length === 0) {
        // Check exclude pages
        if (popup.exclude_pages && popup.exclude_pages.includes(currentPath)) {
          return false
        }
        return true
      }

      // Check if current path matches target pages
      const isTargeted = popup.target_pages.some(targetPath => {
        if (targetPath === '*') return true
        if (targetPath.endsWith('*')) {
          return currentPath.startsWith(targetPath.slice(0, -1))
        }
        return currentPath === targetPath
      })

      // Check exclude pages
      if (isTargeted && popup.exclude_pages && popup.exclude_pages.includes(currentPath)) {
        return false
      }

      return isTargeted
    })
  }

  return data as Popup[]
}

export const getPopupById = async (id: string): Promise<Popup | null> => {
  const { data, error } = await supabase
    .from('popups')
    .select(`
      *,
      category:popup_categories(id, name, color)
    `)
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }

  return data as Popup
}

export const createPopup = async (popup: Omit<Popup, 'id' | 'created_at' | 'updated_at' | 'view_count' | 'click_count' | 'conversion_rate' | 'current_displays'>) => {
  const { data, error } = await supabase
    .from('popups')
    .insert([popup])
    .select(`
      *,
      category:popup_categories(id, name, color)
    `)

  if (error) throw error
  return data[0] as Popup
}

export const updatePopup = async (id: string, updates: Partial<Popup>) => {
  const { data, error } = await supabase
    .from('popups')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      category:popup_categories(id, name, color)
    `)

  if (error) throw error
  return data[0] as Popup
}

export const deletePopup = async (id: string) => {
  const { error } = await supabase
    .from('popups')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Popup Interactions
export const recordPopupInteraction = async (interaction: Omit<PopupInteraction, 'id' | 'created_at'>) => {
  const { error } = await supabase
    .from('popup_interactions')
    .insert([interaction])

  if (error) throw error
}

export const getPopupInteractions = async (popupId?: string, limit = 50) => {
  let query = supabase
    .from('popup_interactions')
    .select(`
      *,
      popup:popups(id, title)
    `)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (popupId) {
    query = query.eq('popup_id', popupId)
  }

  const { data, error } = await query

  if (error) throw error
  return data as PopupInteraction[]
}

// Popup Settings
export const getPopupSettings = async () => {
  const { data, error } = await supabase
    .from('popup_settings')
    .select('*')
    .order('setting_key', { ascending: true })

  if (error) throw error
  return data as PopupSettings[]
}

export const getPopupSetting = async (key: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from('popup_settings')
    .select('setting_value')
    .eq('setting_key', key)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    throw error
  }

  return data.setting_value
}

export const updatePopupSetting = async (key: string, value: string, type: string = 'string') => {
  const { data, error } = await supabase
    .from('popup_settings')
    .upsert({
      setting_key: key,
      setting_value: value,
      setting_type: type
    })
    .select()

  if (error) throw error
  return data[0] as PopupSettings
}

// Popup Statistics
export const getPopupStats = async (): Promise<PopupStats> => {
  // Get basic counts
  const [popupsResult, activePopupsResult, interactionsResult] = await Promise.all([
    supabase.from('popups').select('id', { count: 'exact' }),
    supabase.from('popups').select('id', { count: 'exact' }).eq('is_active', true),
    supabase.from('popup_interactions').select('interaction_type')
  ])

  const total_popups = popupsResult.count || 0
  const active_popups = activePopupsResult.count || 0

  const interactions = interactionsResult.data || []
  const total_views = interactions.filter(i => i.interaction_type === 'viewed').length
  const total_clicks = interactions.filter(i => i.interaction_type === 'clicked').length
  const average_conversion_rate = total_views > 0 ? (total_clicks / total_views) * 100 : 0

  // Get popular popups
  const { data: popularPopupsData } = await supabase
    .from('popups')
    .select('id, title, view_count, click_count')
    .order('view_count', { ascending: false })
    .limit(5)

  const popular_popups = (popularPopupsData || []).map(popup => ({
    ...popup,
    conversion_rate: popup.view_count > 0 ? (popup.click_count / popup.view_count) * 100 : 0
  }))

  // Get recent interactions
  const recent_interactions = await getPopupInteractions(undefined, 10)

  return {
    total_popups,
    active_popups,
    total_views,
    total_clicks,
    average_conversion_rate,
    popular_popups,
    recent_interactions
  }
}

// Course Sessions API Functions

// Get course sessions
export const getCourseSessions = async (courseId?: string, deliveryMode?: 'online' | 'physical', status?: string) => {
  let query = supabase
    .from('course_sessions')
    .select(`
      *,
      course:courses(id, title, level, price),
      created_by_user:admin_users(id, name, email)
    `)

  if (courseId) {
    query = query.eq('course_id', courseId)
  }

  if (deliveryMode) {
    query = query.eq('delivery_mode', deliveryMode)
  }

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query
    .order('start_date', { ascending: true })
    .order('delivery_mode', { ascending: true })

  if (error) throw error
  return data as CourseSession[]
}

// Get available sessions for enrollment
export const getAvailableCourseSessions = async (courseId?: string) => {
  let query = supabase
    .from('course_sessions')
    .select(`
      *,
      course:courses(id, title, level, price)
    `)
    .in('status', ['enrollment_open', 'upcoming'])
    .gte('enrollment_end_date', new Date().toISOString().split('T')[0])

  if (courseId) {
    query = query.eq('course_id', courseId)
  }

  const { data, error } = await query
    .order('start_date', { ascending: true })
    .order('delivery_mode', { ascending: true })

  if (error) throw error
  return data as CourseSession[]
}

// Create course session
export const createCourseSession = async (session: Omit<CourseSession, 'id' | 'created_at' | 'updated_at' | 'current_enrollment'>) => {
  const { data, error } = await supabase
    .from('course_sessions')
    .insert([{ ...session, current_enrollment: 0 }])
    .select(`
      *,
      course:courses(id, title, level, price),
      created_by_user:admin_users(id, name, email)
    `)

  if (error) throw error
  return data[0] as CourseSession
}

// Update course session
export const updateCourseSession = async (id: string, updates: Partial<CourseSession>) => {
  const { data, error } = await supabase
    .from('course_sessions')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      course:courses(id, title, level, price),
      created_by_user:admin_users(id, name, email)
    `)

  if (error) throw error
  return data[0] as CourseSession
}

// Delete course session
export const deleteCourseSession = async (id: string) => {
  const { error } = await supabase
    .from('course_sessions')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Update session enrollment count
export const updateSessionEnrollment = async (sessionId: string, increment: number = 1) => {
  const { data, error } = await supabase.rpc('update_session_enrollment', {
    session_id: sessionId,
    increment_by: increment
  })

  if (error) throw error
  return data
}

// Get session enrollment count
export const getSessionEnrollmentCount = async (sessionId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('admission_applications')
    .select('*', { count: 'exact', head: true })
    .eq('session_id', sessionId)
    .eq('status', 'approved')

  if (error) throw error
  return count || 0
}

// Utility function to check if popup should be shown based on frequency and user session
export const shouldShowPopup = async (popup: Popup, _sessionId: string, _userId?: string): Promise<boolean> => {
  if (!popup.is_active) return false

  // Check scheduling
  const now = new Date()
  if (popup.start_date && new Date(popup.start_date) > now) return false
  if (popup.end_date && new Date(popup.end_date) < now) return false

  // Check max displays
  if (popup.max_displays && popup.current_displays && popup.current_displays >= popup.max_displays) {
    return false
  }

  // Check frequency rules
  const storageKey = `nwlc_popup_${popup.id}`
  const storedTimestamp = Number(
    popup.show_frequency === 'once_per_session'
      ? sessionStorage.getItem(storageKey)
      : localStorage.getItem(storageKey)
  )

  switch (popup.show_frequency) {
    case 'once_per_session':
      return !storedTimestamp

    case 'once_per_day':
      return !storedTimestamp || new Date(storedTimestamp).toDateString() !== new Date().toDateString()

    case 'once_per_user':
      return !storedTimestamp

    case 'always':
    default:
      return true
  }
}
